import React, { useRef, useEffect, useState, useCallback } from 'react';
import './signaturePad.css';

// A simple draw-to-sign canvas modal. Captures mouse + touch strokes and returns
// a trimmed transparent-background PNG data URL via onSave. Reusable by both the
// generated-document editor and the uploaded-PDF signer.
const SignaturePad = ({ onSave, onCancel, penColor = '#0d1020' }) => {
    const canvasRef = useRef(null);
    const drawing = useRef(false);
    const last = useRef({ x: 0, y: 0 });
    const [hasInk, setHasInk] = useState(false);

    // Size the canvas backing store to its CSS box (accounting for DPR) once mounted.
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        const ctx = canvas.getContext('2d');
        ctx.scale(dpr, dpr);
        ctx.lineWidth = 2.5;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.strokeStyle = penColor;
    }, [penColor]);

    const pointerPos = (e) => {
        const rect = canvasRef.current.getBoundingClientRect();
        const touch = e.touches ? e.touches[0] : e;
        return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
    };

    const start = useCallback((e) => {
        e.preventDefault();
        drawing.current = true;
        last.current = pointerPos(e);
    }, []);

    const move = useCallback((e) => {
        if (!drawing.current) return;
        e.preventDefault();
        const ctx = canvasRef.current.getContext('2d');
        const p = pointerPos(e);
        ctx.beginPath();
        ctx.moveTo(last.current.x, last.current.y);
        ctx.lineTo(p.x, p.y);
        ctx.stroke();
        last.current = p;
        if (!hasInk) setHasInk(true);
    }, [hasInk]);

    const end = useCallback(() => { drawing.current = false; }, []);

    const clear = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        setHasInk(false);
    };

    const save = () => {
        if (!hasInk) return;
        onSave(canvasRef.current.toDataURL('image/png'));
    };

    return (
        <div className="sigpad-overlay" onClick={onCancel}>
            <div className="sigpad-modal" onClick={(e) => e.stopPropagation()}>
                <div className="sigpad-header">
                    <h3>Draw signature</h3>
                    <button className="sigpad-close" onClick={onCancel}>&times;</button>
                </div>
                <div className="sigpad-canvas-wrap">
                    <canvas
                        ref={canvasRef}
                        className="sigpad-canvas"
                        onMouseDown={start}
                        onMouseMove={move}
                        onMouseUp={end}
                        onMouseLeave={end}
                        onTouchStart={start}
                        onTouchMove={move}
                        onTouchEnd={end}
                    />
                    <div className="sigpad-baseline" />
                </div>
                <div className="sigpad-actions">
                    <button className="btn-cancel" onClick={clear} type="button">Clear</button>
                    <div style={{ flex: 1 }} />
                    <button className="btn-cancel" onClick={onCancel} type="button">Cancel</button>
                    <button className="btn-primary" onClick={save} disabled={!hasInk} type="button">Apply Signature</button>
                </div>
            </div>
        </div>
    );
};

export default SignaturePad;
